import { createFileRoute, Link, Outlet, useRouterState } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  Bell,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  Search,
  Store,
  User as UserIcon,
  Wifi,
} from "lucide-react";

import { adminNavGroups } from "@/components/admin/nav-sections";
import logo from "@/assets/logo.png";
import { authClient, sessionQueryOptions } from "@/lib/auth-client";
import { memberHasPermission, StaffPermission } from "@pos-vidyon/api/permissions";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { orpc } from "@/utils/orpc";

export const Route = createFileRoute("/_auth/admin")({
  component: AdminLayout,
});

function AdminLayout() {
  const { session } = Route.useRouteContext();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const userName = session.data?.user.name ?? "Liam Basil";
  const { data: activeOrg } = authClient.useActiveOrganization();
  const { data: orgsData } = authClient.useListOrganizations();
  const orgs = orgsData || [];
  const [isAutoSettingOrg, setIsAutoSettingOrg] = useState(false);
  const queryClient = useQueryClient();

  const { data: activeMember } = authClient.useActiveMember();

  const { data: customRoles = [] } = useQuery(orpc.restaurant.getStaffRoles.queryOptions());

  const filteredNavGroups = adminNavGroups
    .map((group) => {
      const items = group.items.filter((item) => {
        if (!activeMember) return false;
        if (item.id === "menu" || item.id === "qr-menu") {
          return memberHasPermission(activeMember, StaffPermission.MANAGE_MENU, customRoles);
        }
        if (item.id === "staff") {
          return memberHasPermission(activeMember, StaffPermission.MANAGE_STAFF, customRoles);
        }
        return true;
      });
      return { ...group, items };
    })
    .filter((group) => group.items.length > 0);

  const handleOrgChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const orgId = e.target.value;
    if (orgId) {
      await authClient.organization.setActive({
        organizationId: orgId,
      });
      queryClient.invalidateQueries({ queryKey: sessionQueryOptions.queryKey });
    }
  };

  useEffect(() => {
    if (!activeOrg && orgs.length > 0 && !isAutoSettingOrg) {
      setIsAutoSettingOrg(true);
      authClient.organization
        .setActive({
          organizationId: orgs[0].id,
        })
        .then(() => {
          queryClient.invalidateQueries({ queryKey: sessionQueryOptions.queryKey });
        })
        .catch((err) => {
          console.error("[AdminLayout] Failed to auto-set active organization:", err);
        })
        .finally(() => setIsAutoSettingOrg(false));
    }
  }, [activeOrg, orgs, isAutoSettingOrg, queryClient]);

  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 1000 * 30);
    return () => clearInterval(interval);
  }, []);

  const initials = userName
    .split(" ")
    .map((part: string) => part[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  if (!activeOrg && isAutoSettingOrg) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 bg-[#f4f5f6] font-sans text-gray-900">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600" />
        <p className="text-sm font-semibold text-gray-600">Restoran ayarları yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden bg-gray-50 font-sans antialiased text-gray-900">
      {/* Top Header Bar */}
      <header className="flex h-[72px] shrink-0 items-center justify-between gap-4 border-b border-gray-200/80 bg-white px-6 shadow-sm z-20">
        <div className="flex items-center gap-2.5">
          <img src={logo} alt="Vidyon Logo" className="h-8 w-auto object-contain" />
          <div className="h-5 w-px bg-gray-200" />
          <div className="flex flex-col justify-center">
            <span className="text-sm font-extrabold tracking-tight text-gray-900 leading-none">
              {activeOrg?.name ?? "Yönetici Paneli"}
            </span>
            {activeOrg?.slug && (
              <span
                className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mt-1 select-all animate-in fade-in duration-300"
                title="Restoran Kodu"
              >
                Kod: {activeOrg.slug}
              </span>
            )}
          </div>

          {orgs.length > 1 && (
            <>
              <div className="h-5 w-px bg-gray-200" />
              <div className="flex items-center gap-1.5 rounded-lg bg-gray-50 border border-gray-200 pl-2.5 pr-1 py-1 text-xs font-semibold text-gray-700">
                <Store className="size-3.5 text-gray-500 shrink-0" />
                <select
                  value={activeOrg?.id || ""}
                  onChange={handleOrgChange}
                  className="bg-transparent focus:outline-none pr-4 font-semibold text-gray-800 cursor-pointer text-xs border-0 py-0"
                >
                  <option value="" disabled>
                    -- Restoran Seç --
                  </option>
                  {orgs.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}
        </div>

        {/* Global Search Bar */}
        <div className="flex min-w-0 max-w-md flex-1 items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-gray-400">
          <Search className="size-4 shrink-0" />
          <input
            type="text"
            placeholder="Ayarlarda ara..."
            className="min-w-0 flex-1 bg-transparent text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none"
          />
          <kbd className="shrink-0 rounded-md border border-gray-200 bg-white px-1.5 py-0.5 text-[11px] font-medium text-gray-400">
            Ctrl K
          </kbd>
        </div>

        {/* Action Widgets */}
        <div className="flex shrink-0 items-center gap-4">
          <Link
            to="/tables"
            className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-bold text-gray-600 hover:bg-gray-50 shadow-sm transition-all"
          >
            <Store className="size-3.5" />
            <span>POS Terminaline Dön</span>
          </Link>

          <div className="h-8 w-px bg-gray-200" />

          <div className="flex items-center gap-2 rounded-lg border border-gray-200/80 px-3 py-1 bg-gray-50/50">
            <Clock className="size-3.5 text-gray-400" />
            <span className="text-xs font-semibold text-gray-700">
              {now.toLocaleDateString("tr-TR", { month: "short", day: "numeric", year: "numeric" })}
              {" • "}
              {now.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}
            </span>
          </div>

          <button className="relative text-gray-500 hover:text-gray-800">
            <Bell className="size-5" />
            <span className="absolute -top-1.5 -right-1.5 flex size-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
              3
            </span>
          </button>
          <Wifi className="size-5 text-emerald-500" />

          <div className="h-8 w-px bg-gray-200" />

          <button className="flex items-center gap-2.5">
            {session.data?.user.image ? (
              <img
                src={session.data.user.image}
                alt={userName}
                className="size-9 rounded-full object-cover border border-gray-100 shadow-sm"
              />
            ) : (
              <div className="flex size-9 items-center justify-center rounded-full bg-gradient-to-tr from-gray-700 to-gray-500 text-xs font-bold text-white shadow-sm">
                {initials || <UserIcon className="size-4" />}
              </div>
            )}
            <span className="text-left leading-tight hidden lg:block">
              <span className="block text-sm font-semibold text-gray-900">{userName}</span>
              <span className="block text-[11px] text-gray-400">Manager</span>
            </span>
            <ChevronDown className="size-3.5 text-gray-400" />
          </button>
        </div>
      </header>

      {/* Main Panel layout */}
      <div className="flex flex-1 overflow-hidden p-5 gap-4">
        {/* Left Sidebar */}
        <aside
          className={`shrink-0 rounded-2xl border border-gray-200 bg-white p-3 shadow-sm z-10 transition-all duration-300 flex flex-col overflow-x-hidden ${
            isSidebarCollapsed ? "w-[72px]" : "w-[260px]"
          }`}
        >
          {isSidebarCollapsed ? (
            <div className="flex justify-center py-1.5 border-b border-gray-100 mb-3 shrink-0">
              <button
                onClick={() => setIsSidebarCollapsed(false)}
                className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-50 hover:text-gray-600 transition-colors focus:outline-none"
                title="Menüyü Genişlet"
              >
                <ChevronRight className="size-4 shrink-0" />
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-between px-3 py-1.5 mb-3 shrink-0">
              <h3 className="text-[11px] font-bold uppercase tracking-wider text-gray-400">
                Yönetim
              </h3>
              <button
                onClick={() => setIsSidebarCollapsed(true)}
                className="rounded-lg p-1 text-gray-400 hover:bg-gray-50 hover:text-gray-600 transition-colors focus:outline-none"
                title="Menüyü Daralt"
              >
                <ChevronLeft className="size-4 shrink-0" />
              </button>
            </div>
          )}

          <div className="flex-1 space-y-4 overflow-y-auto pr-0.5">
            {filteredNavGroups.map((group) => (
              <div key={group.groupLabel}>
                {!isSidebarCollapsed && (
                  <h4 className="px-3 text-[10px] font-bold uppercase tracking-[0.12em] text-gray-400 mb-1">
                    {group.groupLabel}
                  </h4>
                )}
                <div className="space-y-0.5">
                  {group.items.map((section) => {
                    const isActive = section.href && pathname === section.href;
                    const isEnabled = Boolean(section.href);
                    const Icon = section.icon;

                    if (!isEnabled) {
                      return (
                        <div
                          key={section.id}
                          title={isSidebarCollapsed ? `${section.label}` : undefined}
                          className={`flex items-center rounded-lg text-sm font-medium text-gray-400 cursor-not-allowed ${
                            isSidebarCollapsed
                              ? "justify-center p-2.5"
                              : "justify-between px-3 py-2.5"
                          }`}
                        >
                          <span className="flex items-center gap-2.5">
                            <span className="relative">
                              <Icon className="size-4 text-gray-300 shrink-0" />
                              <span className="absolute -top-1 -right-1 flex size-2.5 items-center justify-center">
                                <span className="size-1.5 rounded-full bg-gray-300" />
                              </span>
                            </span>
                            {!isSidebarCollapsed && (
                              <span className="text-gray-400">{section.label}</span>
                            )}
                          </span>
                          {!isSidebarCollapsed && (
                            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-gray-400">
                              Çok Yakında
                            </span>
                          )}
                        </div>
                      );
                    }

                    return (
                      <Link
                        key={section.id}
                        to={section.href}
                        title={isSidebarCollapsed ? section.label : undefined}
                        className={`flex items-center rounded-lg transition-all ${
                          isSidebarCollapsed ? "justify-center p-2.5" : "gap-2.5 px-3 py-2.5"
                        } text-sm font-medium ${
                          isActive
                            ? "bg-gray-100 text-gray-900 font-semibold"
                            : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                        }`}
                      >
                        <Icon
                          className={`size-4 shrink-0 ${
                            isActive ? "text-gray-900" : "text-gray-400"
                          }`}
                        />
                        {!isSidebarCollapsed && <span>{section.label}</span>}
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </aside>

        {/* Section content */}
        <main className="flex-1 min-w-0 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
